import React from 'react';
import cn from 'classnames';
import _ from 'lodash';

import { getPrettyNumber } from '../../helpers/formatting';
import { LINKS_TO_CHECK_TABS, getLinkChips, getLinkDifferences, getLinkQuestion, getSuggestionSentence } from '../../helpers/links_to_check_helpers';
import { getLinkHint } from '../../helpers/links_to_check_hints';
import { useLinksToCheckContext } from './LinksToCheckContext';
import LinkCardActions from './LinkCardActions';
import LinkCardCatalog from './LinkCardCatalog';
import LinkCardPage from './LinkCardPage';

import './LinkCard.scss';

/** The link on screen: its question, both sides, what differs, one tip and its answers. Stands in for the queue's state when there is no link. */
const LinkCard = () => {
  const { link, status, claim, counts, question, setQuestion, site, setSite, reload } = useLinksToCheckContext();

  if (status === 'loading') {
    return (
      <section className='links-to-check-card loading' aria-busy='true' aria-label='Loading links'>
        <div className='card-head'>
          <div className='skeleton-lines'>
            <div className='skeleton-line wide' />
            <div className='skeleton-line' />
          </div>
        </div>
        <div className='card-body'>
          {_.range(2).map(side => (
            <div key={side} className='skeleton-side'>
              <div className='skeleton-photo' />
              <div className='skeleton-lines'>
                <div className='skeleton-line' />
                <div className='skeleton-line short' />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (status !== 'ready') {
    const message = getQueueMessage({ status, site, counts, question });
    const messageActions = { reload, showAllSites: () => setSite(''), goToTab: () => setQuestion(message.action.question) };
    return (
      <section className='links-to-check-card message' role='status'>
        <h2>{message.title}</h2>
        {message.detail && <p>{message.detail}</p>}
        {message.action && (
          <button type='button' className='ltc-btn primary' onClick={messageActions[message.action.kind]}>
            {message.action.label}
          </button>
        )}
      </section>
    );
  }

  // Display data
  const isPair = link.cardType === 'same_item';
  const questionText = getLinkQuestion(link);
  const suggestion = getSuggestionSentence(link);
  const differences = getLinkDifferences({ page: link.page, product: link.pick || link.applied?.product });
  const hint = getLinkHint(link);
  const chips = [
    ...getLinkChips(link),
    ...(claim?.state === 'yours' ? [{ key: 'yours', label: 'Yours', title: 'No one else gets this link while it’s open here. It renews every minute.' }] : [])
  ];

  return (
    <section className={cn('links-to-check-card', { pair: isPair })} aria-label={questionText}>
      <header className='card-head'>
        <div className='card-heading'>
          <h2>{questionText}</h2>
          {suggestion && <p>{suggestion}</p>}
        </div>
        <div className='card-chips'>
          {chips.map(chip => (
            <span key={chip.key} className={cn('card-chip', _.kebabCase(chip.key))} title={chip.title}>
              {chip.label}
            </span>
          ))}
        </div>
      </header>
      <div className='card-body'>
        {!isPair && <LinkCardPage />}
        <LinkCardCatalog />
      </div>
      {differences.length > 0 && (
        <div className='card-differences'>
          {differences.map(difference => (
            <span key={difference.key} className={cn('card-difference', difference.status)}>
              {difference.text}
            </span>
          ))}
        </div>
      )}
      {hint && (
        <div className='card-tip'>
          <b>TIP</b>
          <span>{hint}</span>
        </div>
      )}
      <LinkCardActions />
    </section>
  );
};

export default LinkCard;

const getQueueMessage = ({ status, site, counts, question }) => {
  if (status === 'error') return { title: 'Couldn’t load links.', action: { kind: 'reload', label: 'Try again' } };
  if (status === 'with_teammates') return { title: 'The rest of these are open with teammates right now.', detail: 'Checking again shortly.' };
  if (site) return { title: `No more ${site} links here.`, detail: 'That was the last one from this site in this tab.', action: { kind: 'showAllSites', label: 'Show all sites' } };
  const nextTab = LINKS_TO_CHECK_TABS.find(tab => tab.question !== question && counts[tab.question]?.waiting > 0);
  if (!nextTab) return { title: 'All caught up.', detail: 'New links come in through the day.' };
  return {
    title: 'Nothing waiting here.',
    detail: `${nextTab.label} has ${getPrettyNumber(counts[nextTab.question].waiting)} waiting.`,
    action: { kind: 'goToTab', label: `Go to ${nextTab.label}`, question: nextTab.question }
  };
};
